import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import {
  concat,
  exhaustMap,
  filter,
  map,
  mergeMap,
  Observable,
  of,
} from 'rxjs';

import {
  SupplierActions,
  SupplierApiActions,
  SupplierSelectorsFacade,
} from '@ps/survey-data-access';
import {
  BuyerCpiRepricingSource,
  buyerCpiRepricingUpdate,
  SupplierAllocation,
  SupplierManagementService,
  Survey,
} from '@ps/survey-utils';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import type { Nullable } from '@ps/utils';

import { SurveyCreationAudienceActions } from '../audience/survey-creation-audience.actions';
import {
  selectMarginForm,
  selectMetadataForm,
  selectSuppliersMetaData,
} from '../audience/survey-creation-audience.selectors';
import { SurveyCreationCoreActions } from '../core/survey-creation-core.actions';
import { selectSurvey } from '../core/survey-creation-core.selectors';
import { surveySaved$ } from '../core/utils/save-outcome.helper';
import { selectIsSurveySaveable } from '../core/utils/survey-save-pipeline.helper';
import { SurveyCreationInsightsActions } from './survey-creation-insights.actions';
import { OfferPriceApply } from './utils/survey-creation-apply-offer-price.types';

// Always refreshes recalculation; never announces the drawer's own success state is the feedback.
const SAVE_AFTER_CPI_APPLY = SurveyCreationCoreActions.saveSurvey({
  silent: true,
});

@Injectable()
export class SurveyCreationApplyOfferPriceEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _supplierSelectors = inject(SupplierSelectorsFacade);
  private readonly _supplierEngine = inject(SupplierManagementService);
  private readonly _userSettings = inject(UserSettingsSelectorsFacade);

  // A Buyer CPI the operator confirmed prices every supplier row, the way a global
  // price override does, and their SCPI follows it at the target margin, so the
  // supplier drawer opens on both. That holds under the manual SCPI override too:
  // the margin is the reference, so a hand-typed SCPI is re-priced with the new
  // Buyer CPI rather than left to drift from it — the override stays on. Locally
  // only, like the PurePrice recompute's broadcast, except under that override
  // (see `_repricingPut`): the survey save or the drawer's Apply is what reaches
  // the backend otherwise.
  public readonly broadcastBuyerCpiToAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.applyBuyerCpi),
      concatLatestFrom(() => [
        this._store.select(selectMarginForm),
        this._store.select(selectSurvey),
        this._store.select(selectMetadataForm),
        this._store.select(selectSuppliersMetaData),
      ]),
      map(([{ offerPrice }, margin, survey, metadata, suppliersMetaData]) => ({
        offerPrice,
        survey,
        allocations: this._supplierSelectors.allocations(),
        targetMargin: Option.from(margin.value?.targetMargin),
        loi: metadata.value?.loi ?? null,
        ir: metadata.value?.incidence ?? null,
        blendId: suppliersMetaData.blendId,
        supplierDefaultAllocation: suppliersMetaData.supplierDefaultAllocation,
        defaultPurePriceOverride: suppliersMetaData.defaultPurePriceOverride,
      })),
      filter(({ allocations }) => allocations.length > 0),
      mergeMap(
        ({ offerPrice, survey, allocations, targetMargin, ...source }) => {
          const priced =
            this._supplierEngine.applyGlobalPriceOverrideToAllocations(
              allocations,
              offerPrice,
            );
          const repriced = this._withPdsScpiAtCpi(
            targetMargin.mapOr(priced, (margin) =>
              this._withScpiAtMargin(priced, margin),
            ),
          );

          return [
            SupplierApiActions.loadAllocationsSuccess({
              allocations: repriced,
            }),
            ...this._repricingPut(survey, {
              ...source,
              allocations: repriced,
              buyerCpi: offerPrice,
            }),
          ];
        },
      ),
    ),
  );

  // A Target Margin the operator stood behind re-prices every non-PDS supplier's
  // SCPI, so the supplier drawer opens on it even before the survey is saved —
  // the backend does the same on the save. Local only, no PUT.
  public readonly broadcastTargetMarginToAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.settleTargetMargin),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      filter(([, survey]) => !this._isManualScpiOverride(survey)),
      map(([{ targetMargin }]) => ({
        targetMargin,
        allocations: this._supplierSelectors.allocations(),
      })),
      filter(({ allocations }) => allocations.length > 0),
      map(({ targetMargin, allocations }) =>
        SupplierApiActions.loadAllocationsSuccess({
          allocations: this._withScpiAtMargin(allocations, targetMargin),
        }),
      ),
    ),
  );

  public readonly applyOfferPrice$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.applyOfferPrice),
      concatLatestFrom(() => this._store.select(selectIsSurveySaveable)),
      exhaustMap(([apply, isSaveable]) =>
        isSaveable ? this._applyThenSave(apply) : of(SAVE_AFTER_CPI_APPLY),
      ),
    ),
  );

  // The survey save carries the new offer price, and a save that is first to
  // carry it is re-priced by the backend through a call with no manual SCPI
  // override key, which switches that override off. So a survey holding the
  // override gets the new Buyer CPI through the suppliers API now, ahead of the
  // save — the same order the legacy UI follows — with the override kept on and
  // the re-priced SCPIs persisted. A failed PUT leaves the local prices as they
  // are; the suppliers effect already toasts it.
  private _repricingPut(
    survey: Nullable<Survey>,
    source: Omit<BuyerCpiRepricingSource, 'survey'>,
  ): Action[] {
    return Option.from(survey)
      .filter((current) => current.isManualScpiOverrideEnabled)
      .mapOr<Action[], Action[]>([], (current) => [
        SupplierActions.updateManagedAllocations({
          surveyId: current.id,
          update: buyerCpiRepricingUpdate({ ...source, survey: current }),
        }),
      ]);
  }

  private _isManualScpiOverride(survey: Nullable<Survey>): boolean {
    return Option.from(survey).mapOr(
      false,
      (current) => current.isManualScpiOverrideEnabled,
    );
  }

  // A PDS supplier has no margin, so its SCPI follows the CPI the Buyer CPI just
  // moved, whatever the form's margin is.
  private _withPdsScpiAtCpi(
    allocations: readonly SupplierAllocation[],
  ): SupplierAllocation[] {
    return this._supplierEngine.applyPdsScpiToAllocations(
      allocations,
      this._userSettings.currencySettings(),
    );
  }

  private _withScpiAtMargin(
    allocations: readonly SupplierAllocation[],
    targetMargin: number,
  ): SupplierAllocation[] {
    return this._supplierEngine.applyTargetMarginToAllocations(
      allocations,
      targetMargin,
      this._userSettings.currencySettings(),
    );
  }

  private _applyThenSave({
    pricing,
    surveyId,
    allocationsUpdate,
  }: OfferPriceApply): Observable<Action> {
    return concat(
      of(SurveyCreationInsightsActions.applyCpiPricing(pricing)),
      of(
        SurveyCreationAudienceActions.setSuppliersMetaData({
          metaData: {
            defaultPurePriceOverride:
              allocationsUpdate.context.defaultPurePriceOverride,
          },
        }),
      ),
      of(SAVE_AFTER_CPI_APPLY),
      surveySaved$(this._actions$).pipe(
        map(() =>
          SupplierActions.updateManagedAllocations({
            surveyId,
            update: allocationsUpdate,
          }),
        ),
      ),
    );
  }
}
